import React, { useState } from 'react';
import { Check } from '../FormControls';
import Field from '../Field';
import Button from '../Button';
import ConfirmDialog from '../ConfirmDialog';
import { DateInput } from '../RegionInputs';
import {
  mileageMessage as m, rateDraft, rateMoneyText, rateText, rateUnitLabel, updateExistingDone, updateExistingMessage,
} from '../../domain/mileage';
import { currencySymbol } from '../../domain/money';
import { formatDateOnly, shownDecimal } from '../../domain/locale';
import { todayISO } from '../../domain/clock';
import { blurOnEnter, useDraft } from './useCommitted';
import s from './settings.module.css';

// Business Settings → Mileage Log (#520).
//
// ── What MMS shows ──────────────────────────────────────────────────────────
//   Mileage Expenses
//   Set up automatic expense calculations for mileage log entries
//   [ ] Automatically create expense entries
// and, ticked:
//   Rate              [ £ 0.0000 ] per mile        (per km by the Region)
//   Expense Category  [ dropdown of the expense categories ]
// and only once the rate or the category has been changed (#554):
//   Would you like to update mileage expenses for trips that were already
//   entered?
//   [ ] Yes, update the existing mileage log entries
// which ticked reveals
//   Starting On       [ today ]
// — trips from that date on are rewritten at the new rate and category, and
// gain an expense where they had none. Unticking "Automatically create
// expense entries" clears the stored rate to 0, as MMS's does; the expenses
// already made stay.
//
// The words are core/src/mileage.rs's, which also says what the expense
// carries and how it is rounded.
//
// ── One knowing difference ─────────────────────────────────────────────────
// MMS's "Yes" is a checkbox its panel's Save Changes acts on. This screen
// has no Save button — every control commits as it changes (the top of
// SettingsPage.jsx says why) — so the box and Starting On are MMS's, and
// what acts on them is a button, "Update Mileage Expenses", which asks first,
// naming how many trips from that date and at what. "Changed" is changed
// during this visit to the tab: with no Save there is no unsaved edit to
// measure against, and a rate set yesterday is not one the teacher is in the
// middle of changing. What the box does when the setting is unticked was not
// read, so unticking offers no update.
//
// The rate commits when the box is left, as the hold-days box does: "0.67"
// passes through "0" on the way in. What it commits is `rate_draft`'s — the
// rate it reads as, or the stored one for an empty box or words, so the box
// springs back rather than saving nothing.
//
// The box and the confirmation's sentence write the rate with the Region's
// decimal separator — `0,6700` in Germany, as MMS's box does (#549); the core
// reads either back (`parse_rate`).
//
// `countFrom(date)` is how many trip dates from `date` on the update would
// write, and `onUpdateExisting(date)` writes them, answering how many.
export default function MileagePanel({
  mileage, unit, currency, locale, categories = [], countFrom = () => 0, onChange, onUpdateExisting,
}) {
  const stored = mileage.rateTenThousandths || 0;
  const shownRate = (r) => shownDecimal(rateText(r), locale);
  // Follows the record when it changes underneath — another device, or the
  // first snapshot arriving after this mounted — without the late reset that
  // lost a first keystroke (`useDraft`, #429).
  const [draft, setDraft] = useDraft(shownRate(stored));
  // Whether the rate or the category was changed on this visit (#554).
  const [changed, setChanged] = useState(false);
  const commitRate = () => {
    const next = rateDraft(draft, stored);
    setDraft(shownRate(next));
    if (next !== stored) {
      onChange({ rateTenThousandths: next });
      setChanged(true);
    }
  };

  const [updating, setUpdating] = useState(false);
  const [from, setFrom] = useState(() => todayISO());
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState(null);

  const symbol = currencySymbol(currency, locale);
  // The confirmation's rate is the Region's money format (#603), not the
  // Rate box's own MMS affix — rateMoneyText's header says why they differ.
  const rateShown = rateMoneyText(stored, currency, locale, unit);
  const category = mileage.expenseCategory || '';
  // A rename or a delete carries this setting with it
  // (`useExpenses.renameCategory`/`removeCategory`, #555); this is a backstop
  // for a document without that cascade, kept on the list so the select says
  // so rather than showing the first option as though it were chosen.
  const options = category && !categories.includes(category) ? [category, ...categories] : categories;
  const count = mileage.autoExpenses && stored > 0 && from ? countFrom(from) : 0;

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>{m('expensesSection')}</div>
      <p className={s.blurb}>{m('expensesSubtitle')}</p>

      <div className={s.group}>
        <Check
          checked={mileage.autoExpenses}
          // Unticked, the rate goes back to 0, as MMS's does (#554).
          onChange={(e) => onChange(e.target.checked
            ? { autoExpenses: true }
            : { autoExpenses: false, rateTenThousandths: 0 })}
          label={m('autoExpenses')}
        />
      </div>

      {mileage.autoExpenses ? (
        <div className={s.form}>
          <Field label={m('rate')} htmlFor="mileage-rate">
            <div className={s.numberRow}>
              <span className={s.unit}>{symbol}</span>
              <input
                id="mileage-rate"
                className={s.rateInput}
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitRate}
                onKeyDown={blurOnEnter}
              />
              <span className={s.unit}>{rateUnitLabel(unit)}</span>
            </div>
          </Field>

          <Field label={m('expenseCategory')} htmlFor="mileage-expense-category">
            <select
              id="mileage-expense-category"
              className={s.windowSelect}
              value={category}
              onChange={(e) => { onChange({ expenseCategory: e.target.value || null }); setChanged(true); }}
            >
              <option value="">{m('chooseCategory')}</option>
              {options.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          {changed ? (
            <div>
              <div className={s.groupLabel}>{m('updateExistingQuestion')}</div>
              <Check
                checked={updating}
                onChange={(e) => { setUpdating(e.target.checked); setDone(null); }}
                label={m('updateExisting')}
              />
              {updating ? (
                <div className={s.form}>
                  <Field label={m('startingOn')} htmlFor="mileage-starting-on">
                    <DateInput id="mileage-starting-on" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </Field>
                  <div>
                    <Button variant="secondary" disabled={count === 0} onClick={() => { setDone(null); setAsking(true); }}>
                      {m('updateExistingTitle')}
                    </Button>
                  </div>
                </div>
              ) : null}
              {done != null ? <p className={s.note}>{updateExistingDone(done)}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={asking}
        title={m('updateExistingTitle')}
        message={updateExistingMessage(count, formatDateOnly(from, locale), rateShown, category)}
        confirmLabel={m('updateExistingTitle')}
        discardLabel={m('cancel')}
        cancelLabel={null}
        onConfirm={async () => {
          setAsking(false);
          setDone(await onUpdateExisting(from));
          setUpdating(false);
        }}
        onDiscard={() => setAsking(false)}
        onCancel={() => setAsking(false)}
      />
    </div>
  );
}
